import { useCountdown } from '@/hooks/use-countdown';
import type { UpcomingLaunch } from '@vtak/mod-space/client';
import { cn } from '@/lib/utils';

/** Hero card: the next launch with a live T-minus countdown + launch window. */
export function ActiveMissionCard({ launch }: { launch: UpcomingLaunch }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-echo-border bg-echo-grey/80 p-5">
      <div className="flex items-center gap-2">
        <span className="font-chakra text-xs font-bold uppercase tracking-[0.2em] text-echo-yellow">Next Mission</span>
        {launch.webcastLive && <LiveNowBadge />}
      </div>

      <div className="flex gap-4">
        {launch.image && (
          <img src={launch.image} alt="" className="h-24 w-24 shrink-0 rounded-md object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-chakra text-2xl font-semibold text-foreground">
            {launch.mission || launch.name}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {[launch.rocket, launch.provider].filter(Boolean).join('  |  ')}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {[launch.padName, launch.location].filter(Boolean).join(' · ')}
          </p>
          {launch.status && (
            <span className="mt-2 inline-block rounded bg-echo-yellow/15 px-2 py-0.5 text-[11px] font-medium text-echo-yellow">
              {launch.status}
            </span>
          )}
        </div>
      </div>

      <CountdownClock targetIso={launch.net || launch.windowStart} />
      <WindowSlider start={launch.windowStart} end={launch.windowEnd} />
    </div>
  );
}

/** Pulsing "LIVE NOW" badge for a mission whose webcast is streaming. */
function LiveNowBadge(): React.JSX.Element {
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-400">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
      Live Now
    </span>
  );
}

/** Big T-minus clock. */
function CountdownClock({ targetIso }: { targetIso: string }): React.JSX.Element {
  const { h, m, s, past } = useCountdown(targetIso);
  return (
    <div className="rounded-md bg-echo-yellow px-4 py-3 text-center">
      <div className="font-mono text-3xl font-bold tracking-wider text-black">
        {past ? 'LAUNCHED' : `T- ${pad(h)}:${pad(m)}:${pad(s)}`}
      </div>
    </div>
  );
}

/** Progress bar through the launch window (skipped for instantaneous windows). */
function WindowSlider({ start, end }: { start: string; end: string }): React.JSX.Element | null {
  const startTs = new Date(start).getTime();
  const endTs = new Date(end).getTime();
  if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs) return null;

  const percent = Math.min(100, Math.max(0, ((Date.now() - startTs) / (endTs - startTs)) * 100));
  return (
    <div>
      <div className="mb-1 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
        <span>Window {formatTime(startTs)}</span>
        <span>{formatTime(endTs)}</span>
      </div>
      <div className="relative h-1.5 w-full rounded-full bg-echo-border">
        <div className={cn('absolute h-1.5 rounded-full bg-emerald-500')} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/** Zero-pad to two digits. */
function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Local HH:MM. */
function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
